/**
 * Solo Assessment Logic - Learning Profile Builder
 * 
 * This module handles the questionnaire logic and recommendation engine
 * for building learning profiles without storing student data.
 */

// ============================================================================
// PHASE 1: INITIAL PROFILE QUESTIONS
// ============================================================================

export const AGE_GROUP_OPTIONS = [
  { 
    id: 'eyfs', 
    label: 'Early Years (3-5)', 
    description: 'Nursery & Reception',
    ageMin: 3, 
    ageMax: 5,
    phase: 'primary',
    keyStage: 'eyfs'
  },
  { 
    id: 'ks1', 
    label: 'Key Stage 1 (5-7)', 
    description: 'Years 1-2',
    ageMin: 5, 
    ageMax: 7,
    phase: 'primary',
    keyStage: 'ks1'
  },
  { 
    id: 'ks2', 
    label: 'Key Stage 2 (7-11)', 
    description: 'Years 3-6',
    ageMin: 7, 
    ageMax: 11,
    phase: 'primary',
    keyStage: 'ks2'
  },
  { 
    id: 'ks3', 
    label: 'Key Stage 3 (11-14)', 
    description: 'Years 7-9',
    ageMin: 11, 
    ageMax: 14,
    phase: 'secondary',
    keyStage: 'ks3'
  },
  { 
    id: 'ks4', 
    label: 'Key Stage 4 (14-16)', 
    description: 'Years 10-11',
    ageMin: 14, 
    ageMax: 16,
    phase: 'secondary',
    keyStage: 'ks4'
  },
  { 
    id: 'post16', 
    label: 'Post-16 (16-19)', 
    description: 'Sixth Form / College',
    ageMin: 16, 
    ageMax: 19,
    phase: 'post16',
    keyStage: 'post16'
  }
];

export const COGNITIVE_LEVEL_OPTIONS = [
  {
    id: 'at_nc',
    label: 'At or near age expectations',
    description: 'Working broadly at National Curriculum level for their age',
    ncAccess: 'full',
    developmentalTier: 'A'
  },
  {
    id: 'below_1_2',
    label: '1-2 years below expectations',
    description: 'On National Curriculum but needs support to access it',
    ncAccess: 'supported',
    developmentalTier: 'B'
  },
  {
    id: 'below_nc',
    label: 'Working below National Curriculum',
    description: 'Working on foundational skills, may use pre-key stage standards',
    ncAccess: 'below',
    developmentalTier: 'C'
  },
  {
    id: 'pre_formal',
    label: 'Pre-formal / Engagement Model',
    description: 'Working on engagement and early developmental skills',
    ncAccess: 'engagement',
    developmentalTier: 'E'
  }
];

// ============================================================================
// PHASE 2: BRANCHING NEED AREA QUESTIONS
// ============================================================================

// Questions for students working at/near NC (additional support focus)
export const NC_SUPPORT_QUESTIONS = [
  {
    id: 'comm_expression',
    domain: 'Communication',
    text: 'How does this student express themselves in class?',
    options: [
      { value: 'fluent', label: 'Speaks fluently at age-expected level', weight: 0 },
      { value: 'needs_support', label: 'Clear speech but needs support to organise ideas', weight: 1 },
      { value: 'short_sentences', label: 'Uses short sentences, sometimes struggles to be understood', weight: 2 },
      { value: 'simple_words', label: 'Uses simple words/phrases, often relies on gestures', weight: 3 }
    ]
  },
  {
    id: 'comm_understanding',
    domain: 'Communication',
    text: 'How well do they understand instructions?',
    options: [
      { value: 'multi_step', label: 'Follows multi-step instructions consistently', weight: 0 },
      { value: 'one_two_step', label: 'Follows 1-2 step instructions with some support', weight: 1 },
      { value: 'single_step', label: 'Needs single-step instructions with visuals', weight: 2 },
      { value: 'frequent_rep', label: 'Needs frequent repetition and clarification', weight: 3 }
    ]
  },
  {
    id: 'cog_learning',
    domain: 'Cognition',
    text: 'How do they engage with learning tasks?',
    options: [
      { value: 'keeps_pace', label: 'Keeps up with expected pace', weight: 0 },
      { value: 'slightly_behind', label: 'Slightly behind but engages with support', weight: 1 },
      { value: 'needs_scaffolding', label: 'Needs regular adult scaffolding', weight: 2 },
      { value: 'struggles', label: 'Struggles to access learning despite support', weight: 3 }
    ]
  },
  {
    id: 'cog_concentration',
    domain: 'Cognition',
    text: 'How is their concentration during learning?',
    options: [
      { value: 'maintains', label: 'Maintains focus with minimal prompts', weight: 0 },
      { value: 'needs_reminders', label: 'Needs regular reminders and check-ins', weight: 1 },
      { value: 'structured_breaks', label: 'Requires structured breaks and visual supports', weight: 2 },
      { value: 'disengages', label: 'Often disengages, needs 1:1 to access learning', weight: 3 }
    ]
  },
  {
    id: 'semh_regulation',
    domain: 'SEMH',
    text: 'How do they manage their emotions in class?',
    options: [
      { value: 'manages', label: 'Manages emotions well with age-appropriate strategies', weight: 0 },
      { value: 'needs_support_times', label: 'Needs adult support at times but generally self-regulates', weight: 1 },
      { value: 'regular_coregulation', label: 'Regularly needs co-regulation or adult intervention', weight: 2 },
      { value: 'escalates', label: 'Escalates quickly or withdraws, needs structured support', weight: 3 }
    ]
  },
  {
    id: 'semh_transitions',
    domain: 'SEMH',
    text: 'How do they handle transitions and changes?',
    options: [
      { value: 'independent', label: 'Transitions well independently', weight: 0 },
      { value: 'with_reminders', label: 'Manages with reminders or visual support', weight: 1 },
      { value: 'structured_support', label: 'Requires structured adult support for transitions', weight: 2 },
      { value: 'distress', label: 'Experiences significant distress at transitions', weight: 3 }
    ]
  },
  {
    id: 'sensory_responses',
    domain: 'Sensory',
    text: 'How do they respond to sensory input (noise, light, touch)?',
    options: [
      { value: 'typical', label: 'Typical responses', weight: 0 },
      { value: 'mild', label: 'Mild sensitivities but manageable', weight: 1 },
      { value: 'regular_seeking', label: 'Regular sensory seeking or avoidance', weight: 2 },
      { value: 'strong', label: 'Strong sensory responses affecting participation', weight: 3 }
    ]
  },
  {
    id: 'social_interaction',
    domain: 'Communication',
    text: 'How do they interact with peers?',
    options: [
      { value: 'confident', label: 'Participates confidently in social and group activities', weight: 0 },
      { value: 'prompts', label: 'Participates but needs reminders for social rules', weight: 1 },
      { value: 'reluctant', label: 'Reluctant to join groups, prefers adult interaction', weight: 2 },
      { value: 'avoids', label: 'Avoids or becomes distressed in group situations', weight: 3 }
    ]
  }
];

// Questions for students working below NC (developmental focus)
export const ENGAGEMENT_MODEL_QUESTIONS = [
  {
    id: 'eng_communication',
    domain: 'Communication',
    text: 'How does this student communicate their needs?',
    options: [
      { value: 'short_phrases', label: 'Uses short phrases and simple sentences', weight: 1 },
      { value: 'single_words', label: 'Uses single words and gestures', weight: 2 },
      { value: 'non_verbal', label: 'Non-verbal, uses sounds/movements to communicate', weight: 3 },
      { value: 'pre_intentional', label: 'Pre-intentional communication, relies on adult interpretation', weight: 4 }
    ]
  },
  {
    id: 'eng_understanding',
    domain: 'Communication',
    text: 'How well do they understand spoken language?',
    options: [
      { value: 'simple_sentences', label: 'Follows simple sentences with visual support', weight: 1 },
      { value: 'single_step', label: 'Follows single-step instructions with support', weight: 2 },
      { value: 'key_words', label: 'Understands key words with objects/photos', weight: 3 },
      { value: 'minimal', label: 'Minimal understanding, needs physical/sensory cues', weight: 4 }
    ]
  },
  {
    id: 'eng_cognition',
    domain: 'Cognition',
    text: 'How do they engage with learning activities?',
    options: [
      { value: 'concrete', label: 'Works with concrete, hands-on activities', weight: 1 },
      { value: 'cause_effect', label: 'Engages with cause and effect activities', weight: 2 },
      { value: 'sensory_exploration', label: 'Explores through sensory experiences', weight: 3 },
      { value: 'early_awareness', label: 'Developing awareness of surroundings', weight: 4 }
    ]
  },
  {
    id: 'eng_regulation',
    domain: 'SEMH',
    text: 'How do they manage their emotions and behaviour?',
    options: [
      { value: 'co_regulation', label: 'Needs adult co-regulation at times', weight: 1 },
      { value: 'frequent', label: 'Frequently needs adult support to regulate', weight: 2 },
      { value: 'constant', label: 'Needs constant adult support for regulation', weight: 3 },
      { value: 'emerging', label: 'Developing awareness of own emotional states', weight: 4 }
    ]
  },
  {
    id: 'eng_attention',
    domain: 'Cognition',
    text: 'What is their attention like?',
    options: [
      { value: 'shared', label: 'Can share attention between adult and activity', weight: 1 },
      { value: 'fleeting', label: 'Fleeting attention, needs high motivation', weight: 2 },
      { value: 'adult_led', label: 'Attends briefly to adult-led activities', weight: 3 },
      { value: 'sensory', label: 'Attends mainly to sensory stimuli', weight: 4 }
    ]
  },
  {
    id: 'eng_physical',
    domain: 'Sensory',
    text: 'What are their physical/sensory needs?',
    options: [
      { value: 'mild', label: 'Some physical/sensory needs requiring adaptations', weight: 1 },
      { value: 'moderate', label: 'Regular physical support or sensory breaks needed', weight: 2 },
      { value: 'significant', label: 'Significant physical support or sensory diet', weight: 3 },
      { value: 'high', label: 'High physical/sensory needs, specialist equipment', weight: 4 }
    ]
  }
];

// ============================================================================
// CURRICULUM APPROACH DETERMINATION
// ============================================================================

/**
 * Determine if student should follow NC with support or Engagement Model
 * @param {Object} profileAnswers - Age group and cognitive level
 * @returns {'NC_WITH_SUPPORT' | 'ENGAGEMENT_MODEL'}
 */
export function getCurriculumApproach(profileAnswers) {
  const { cognitiveLevel } = profileAnswers;
  
  if (cognitiveLevel === 'pre_formal' || cognitiveLevel === 'below_nc') {
    return 'ENGAGEMENT_MODEL';
  }
  
  return 'NC_WITH_SUPPORT';
}

/**
 * Get the appropriate questions based on curriculum approach
 * @param {string} curriculumApproach - 'NC_WITH_SUPPORT' or 'ENGAGEMENT_MODEL'
 * @returns {Array} Questions to ask
 */
export function getQuestionsForApproach(curriculumApproach) {
  if (curriculumApproach === 'ENGAGEMENT_MODEL') {
    return ENGAGEMENT_MODEL_QUESTIONS;
  }
  return NC_SUPPORT_QUESTIONS;
}

// ============================================================================
// RECOMMENDATION PROFILE GENERATION
// ============================================================================

/**
 * Build a recommendation profile from questionnaire answers
 * @param {Object} profileAnswers - Initial profile (age, cognitive level)
 * @param {Object} questionnaireAnswers - Answers keyed by question ID
 * @returns {Object} Recommendation profile for KB matching
 */
export function getRecommendationProfile(profileAnswers, questionnaireAnswers) {
  const curriculumApproach = getCurriculumApproach(profileAnswers);
  const questions = getQuestionsForApproach(curriculumApproach);
  
  // Get age group info
  const ageGroup = AGE_GROUP_OPTIONS.find(a => a.id === profileAnswers.ageGroup);
  const cogLevel = COGNITIVE_LEVEL_OPTIONS.find(c => c.id === profileAnswers.cognitiveLevel);
  
  // Calculate domain scores from answers
  const domainScores = {
    Communication: 0,
    Cognition: 0,
    SEMH: 0,
    Sensory: 0
  };
  const domainCounts = {
    Communication: 0,
    Cognition: 0,
    SEMH: 0,
    Sensory: 0
  };
  
  // Calculate weighted domain scores
  questions.forEach(question => {
    const answer = questionnaireAnswers[question.id];
    if (answer) {
      const option = question.options.find(o => o.value === answer);
      if (option) {
        domainScores[question.domain] += option.weight;
        domainCounts[question.domain]++;
      }
    }
  });
  
  // Normalize scores (0-3 scale where higher = more need)
  const normalizedScores = {};
  Object.keys(domainScores).forEach(domain => {
    normalizedScores[domain] = domainCounts[domain] > 0 
      ? domainScores[domain] / domainCounts[domain]
      : 0;
  });
  
  // Identify primary need areas (score >= 2)
  const primaryNeeds = Object.entries(normalizedScores)
    .filter(([_, score]) => score >= 2)
    .map(([domain]) => domain);
  
  // Identify secondary needs (score >= 1)
  const secondaryNeeds = Object.entries(normalizedScores)
    .filter(([_, score]) => score >= 1 && score < 2)
    .map(([domain]) => domain);
  
  // Generate tags for KB matching
  const focusTags = generateFocusTags(questionnaireAnswers, curriculumApproach);
  
  return {
    curriculumApproach,
    ageGroup: ageGroup || { ageMin: 5, ageMax: 11 },
    developmentalTier: cogLevel?.developmentalTier || 'B',
    domainScores: normalizedScores,
    primaryNeeds: primaryNeeds.length > 0 ? primaryNeeds : ['Cognition'],
    secondaryNeeds,
    focusTags,
    // Use developmental age for activity matching, not chronological
    effectiveAgeRange: getEffectiveAgeRange(ageGroup, cogLevel)
  };
}

/**
 * Calculate effective age range for activity matching
 * For students with severe needs, use developmental level instead of chronological age
 */
function getEffectiveAgeRange(ageGroup, cogLevel) {
  if (!ageGroup) return { min: 5, max: 11 };
  
  // For engagement model students, widen the age range significantly
  if (cogLevel?.ncAccess === 'engagement') {
    return {
      min: Math.max(3, ageGroup.ageMin - 8),
      max: ageGroup.ageMax
    };
  }
  
  // For below NC, widen somewhat
  if (cogLevel?.ncAccess === 'below') {
    return {
      min: Math.max(3, ageGroup.ageMin - 4),
      max: ageGroup.ageMax
    };
  }
  
  // For NC students, use actual age range
  return {
    min: ageGroup.ageMin,
    max: ageGroup.ageMax
  };
}

/**
 * Generate focus tags for KB matching based on answers
 */
function generateFocusTags(answers, curriculumApproach) {
  const tags = [];
  
  if (curriculumApproach === 'ENGAGEMENT_MODEL') {
    tags.push('engagement-model', 'developmental');
    
    if (answers.eng_communication === 'pre_intentional' || answers.eng_communication === 'non_verbal') {
      tags.push('intensive-interaction', 'pre-verbal', 'AAC');
    }
    if (answers.eng_attention === 'sensory' || answers.eng_attention === 'adult_led') {
      tags.push('attention-autism', 'bucket-time');
    }
    if (answers.eng_cognition === 'sensory_exploration') {
      tags.push('sensory', 'exploration', 'cause-effect');
    }
    if (answers.eng_physical === 'significant' || answers.eng_physical === 'high') {
      tags.push('physical-support', 'positioning', 'sensory-diet');
    }
  } else {
    // NC with support tags
    if (answers.comm_expression === 'simple_words' || answers.comm_expression === 'short_sentences') {
      tags.push('vocabulary', 'speech-language', 'visual-supports');
    }
    if (answers.comm_understanding === 'single_step' || answers.comm_understanding === 'frequent_rep') {
      tags.push('visual-supports', 'scaffolding', 'chunking');
    }
    if (answers.cog_concentration === 'disengages' || answers.cog_concentration === 'structured_breaks') {
      tags.push('attention', 'movement-breaks', 'now-and-next');
    }
    if (answers.semh_regulation === 'escalates' || answers.semh_regulation === 'regular_coregulation') {
      tags.push('zones-of-regulation', 'emotion-coaching', 'co-regulation');
    }
    if (answers.semh_transitions === 'distress' || answers.semh_transitions === 'structured_support') {
      tags.push('transitions', 'visual-timetable', 'social-stories');
    }
    if (answers.sensory_responses === 'strong' || answers.sensory_responses === 'regular_seeking') {
      tags.push('sensory', 'sensory-circuits', 'regulation');
    }
    if (answers.social_interaction === 'avoids' || answers.social_interaction === 'reluctant') {
      tags.push('social-skills', 'small-groups', '1:1');
    }
  }
  
  return [...new Set(tags)];
}

// ============================================================================
// KB ITEM SCORING
// ============================================================================

/**
 * Score a KB item for a given profile
 * @param {Object} profile - Recommendation profile
 * @param {Object} kbItem - KB item to score
 * @returns {number} Score 0-1
 */
export function scoreKBItemForProfile(profile, kbItem) {
  let score = 0;
  let weights = { domain: 0.35, age: 0.25, tags: 0.25, level: 0.15 };
  
  // Domain fit (0-1)
  const domainFit = calculateDomainFit(profile, kbItem);
  score += weights.domain * domainFit;
  
  // Age fit (0-1) - using effective age range
  const ageFit = calculateAgeFit(profile.effectiveAgeRange, kbItem);
  score += weights.age * ageFit;
  
  // Tag overlap (0-1)
  const tagFit = calculateTagFit(profile.focusTags, kbItem.tags || []);
  score += weights.tags * tagFit;
  
  // Level fit (0-1)
  const levelFit = calculateLevelFit(profile.developmentalTier, kbItem);
  score += weights.level * levelFit;
  
  // Boost for engagement model activities when needed
  if (profile.curriculumApproach === 'ENGAGEMENT_MODEL') {
    const engagementTags = ['engagement-model', 'intensive-interaction', 'attention-autism', 'sensory'];
    const hasEngagementTag = (kbItem.tags || []).some(t => 
      engagementTags.some(et => t.toLowerCase().includes(et))
    );
    if (hasEngagementTag) {
      score += 0.15;
    }
  }
  
  return Math.min(1, score);
}

function calculateDomainFit(profile, kbItem) {
  const itemDomains = (kbItem.targetDomains || []).map(d => d.toLowerCase());
  const allNeeds = [...profile.primaryNeeds, ...profile.secondaryNeeds];
  
  if (itemDomains.length === 0 || allNeeds.length === 0) return 0.5;
  
  let matchCount = 0;
  allNeeds.forEach(need => {
    const needLower = need.toLowerCase();
    if (itemDomains.some(d => d.includes(needLower) || needLower.includes(d))) {
      matchCount++;
    }
  });
  
  // Weight primary needs higher
  const primaryMatches = profile.primaryNeeds.filter(need => 
    itemDomains.some(d => d.toLowerCase().includes(need.toLowerCase()))
  ).length;
  
  const baseScore = matchCount / allNeeds.length;
  const primaryBonus = primaryMatches > 0 ? 0.2 : 0;
  
  return Math.min(1, baseScore + primaryBonus);
}

function calculateAgeFit(effectiveAgeRange, kbItem) {
  if (!kbItem.ageRange) return 0.7;
  
  const { min, max } = kbItem.ageRange;
  const profileMin = effectiveAgeRange.min;
  const profileMax = effectiveAgeRange.max;
  
  // Check overlap
  const overlapStart = Math.max(min, profileMin);
  const overlapEnd = Math.min(max, profileMax);
  
  if (overlapEnd >= overlapStart) {
    const overlapSize = overlapEnd - overlapStart;
    const profileRange = profileMax - profileMin;
    return Math.min(1, 0.6 + (overlapSize / profileRange) * 0.4);
  }
  
  // No overlap - calculate penalty
  const gap = overlapStart > overlapEnd ? overlapStart - overlapEnd : 0;
  return Math.max(0, 0.5 - gap * 0.15);
}

function calculateTagFit(profileTags, itemTags) {
  if (profileTags.length === 0 || itemTags.length === 0) return 0.5;
  
  const profileTagsLower = profileTags.map(t => t.toLowerCase());
  const itemTagsLower = itemTags.map(t => t.toLowerCase());
  
  let matchCount = 0;
  profileTagsLower.forEach(pt => {
    if (itemTagsLower.some(it => it.includes(pt) || pt.includes(it))) {
      matchCount++;
    }
  });
  
  return Math.min(1, matchCount / Math.min(profileTags.length, 3));
}

function calculateLevelFit(developmentalTier, kbItem) {
  const appropriateLevels = kbItem.appropriateLevels || [];
  if (appropriateLevels.length === 0) return 0.6;
  
  // Check for tier match
  const tierMap = { 'A': ['P8', 'NC'], 'B': ['P6', 'P7', 'P8'], 'C': ['P4', 'P5', 'P6'], 'E': ['P1', 'P2', 'P3', 'P4'] };
  const appropriateForTier = tierMap[developmentalTier] || [];
  
  const hasMatch = appropriateLevels.some(level => 
    appropriateForTier.some(t => level.toUpperCase().includes(t))
  );
  
  return hasMatch ? 1.0 : 0.4;
}

// ============================================================================
// SUMMARY GENERATION
// ============================================================================

/**
 * Generate a text summary of the assessment
 * @param {Object} profile - Recommendation profile
 * @param {Object} profileAnswers - Original profile answers
 * @returns {string} Human-readable summary
 */
export function generateAssessmentSummary(profile, profileAnswers) {
  const ageLabel = AGE_GROUP_OPTIONS.find(a => a.id === profileAnswers.ageGroup)?.label || 'Unknown age';
  const cogLabel = COGNITIVE_LEVEL_OPTIONS.find(c => c.id === profileAnswers.cognitiveLevel)?.label || 'Unknown level';
  
  let summary = `**Age Group:** ${ageLabel}\n`;
  summary += `**Cognitive Level:** ${cogLabel}\n\n`;
  
  if (profile.curriculumApproach === 'ENGAGEMENT_MODEL') {
    summary += `**Curriculum Approach:** Engagement Model\n`;
    summary += `This student appears to be working below the National Curriculum. The recommended activities form a developmental curriculum to build foundational skills, rather than additional support for NC learning.\n\n`;
  } else {
    summary += `**Curriculum Approach:** National Curriculum with Support\n`;
    summary += `This student appears to be accessing the National Curriculum. The recommended activities provide additional support alongside their regular learning.\n\n`;
  }
  
  if (profile.primaryNeeds.length > 0) {
    summary += `**Primary Need Areas:** ${profile.primaryNeeds.join(', ')}\n`;
  }
  
  if (profile.secondaryNeeds.length > 0) {
    summary += `**Secondary Need Areas:** ${profile.secondaryNeeds.join(', ')}\n`;
  }
  
  return summary;
}

/**
 * Get curriculum approach messaging for display
 * @param {string} approach - 'NC_WITH_SUPPORT' or 'ENGAGEMENT_MODEL'
 * @returns {Object} { title, description, color }
 */
export function getCurriculumApproachDisplay(approach) {
  if (approach === 'ENGAGEMENT_MODEL') {
    return {
      title: 'Engagement Model Curriculum',
      description: 'This student appears to be working below the National Curriculum. The activities below form a developmental curriculum to build foundational skills. These activities would typically replace standard curriculum content rather than supplement it.',
      color: 'purple',
      icon: 'Sparkles'
    };
  }
  
  return {
    title: 'National Curriculum with Support',
    description: 'This student appears to be working on the National Curriculum. The activities below provide additional support alongside their regular learning to help them access and succeed in their education.',
    color: 'blue',
    icon: 'AcademicCap'
  };
}
